import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  applyDashboardClientFilters,
  buildDashboardFilterOptions,
} from '@/components/Gestores/gestorDashboardApplyFilters'
import { defaultDashboardPeriod } from '@/components/Gestores/gestorDashboardFormat'
import { operationalMatchesPeriod } from '@/components/Gestores/gestorDashboardPeriodUtils'
import {
  filtersToPresetCriteria,
  presetCriteriaToFilters,
  type GestorDashboardFilterCatalog,
  type GestorDashboardFilterPreset,
} from '@/components/Gestores/gestorDashboardPresetTypes'
import type {
  GestorDashboardFinancialMetrics,
  GestorDashboardPayload,
} from '@/components/Gestores/gestorDashboardTypes'
import { gestoresService } from '@/services/gestoresService'
import { getFriendlyError } from '@/utils/getFriendlyError'

type Filters = {
  createdFrom: string
  createdTo: string
  squadId: number | null
  squadName: string
  workspaces: string[]
  executors: string[]
  taskType: string
  phase: string
  search: string
}

export function useGestorDashboardTab(enabled: boolean) {
  const defaults = defaultDashboardPeriod()
  const [filters, setFilters] = useState<Filters>({
    createdFrom: defaults.createdFrom,
    createdTo: defaults.createdTo,
    squadId: null,
    squadName: '',
    workspaces: [],
    executors: [],
    taskType: '',
    phase: '',
    search: '',
  })
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [baseOperational, setBaseOperational] = useState<GestorDashboardPayload | null>(null)
  const [financialMetrics, setFinancialMetrics] = useState<GestorDashboardFinancialMetrics | null>(null)
  const [financialPeriodKey, setFinancialPeriodKey] = useState<string | null>(null)
  const [presets, setPresets] = useState<GestorDashboardFilterPreset[]>([])
  const [catalogs, setCatalogs] = useState<GestorDashboardFilterCatalog | null>(null)
  const [activePresetId, setActivePresetId] = useState<string | null>(null)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [editingPreset, setEditingPreset] = useState<GestorDashboardFilterPreset | null>(null)
  const [loading, setLoading] = useState(false)
  const [financialLoading, setFinancialLoading] = useState(false)
  const [presetsLoading, setPresetsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const syncSeq = useRef(0)
  const financialSeq = useRef(0)
  const defaultAppliedRef = useRef(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 350)
    return () => window.clearTimeout(timer)
  }, [filters.search])

  const syncQuery = useMemo(
    () => ({
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      squadId: filters.squadId ?? undefined,
    }),
    [filters.createdFrom, filters.createdTo, filters.squadId],
  )

  const deferredSearch = useDeferredValue(debouncedSearch)

  const clientFilters = useMemo(
    () => ({
      workspaces: filters.workspaces,
      executors: filters.executors,
      taskType: filters.taskType || undefined,
      phase: filters.phase || undefined,
      search: deferredSearch || undefined,
    }),
    [filters.workspaces, filters.executors, filters.taskType, filters.phase, deferredSearch],
  )

  const currentCriteria = useMemo(
    () =>
      filtersToPresetCriteria({
        squadId: filters.squadId,
        squadName: filters.squadName,
        workspaces: filters.workspaces,
        executors: filters.executors,
        taskType: filters.taskType,
        search: filters.search,
      }),
    [filters],
  )

  const periodKey = `${syncQuery.createdFrom}:${syncQuery.createdTo}:${syncQuery.squadId ?? 'all'}`

  const activeOperational = useMemo(
    () =>
      operationalMatchesPeriod(baseOperational, syncQuery.createdFrom, syncQuery.createdTo)
        ? baseOperational
        : null,
    [baseOperational, syncQuery.createdFrom, syncQuery.createdTo],
  )

  const filterOptions = useMemo(() => {
    if (!activeOperational) {
      return { executors: [], workspaces: [], taskTypes: [] }
    }
    return buildDashboardFilterOptions(activeOperational)
  }, [activeOperational])

  const refreshPresets = useCallback(async () => {
    if (!enabled) return
    setPresetsLoading(true)
    try {
      const rows = await gestoresService.listDashboardFilterPresets({
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
      })
      setPresets(rows)
      return rows
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível carregar os filtros salvos'))
      return []
    } finally {
      setPresetsLoading(false)
    }
  }, [enabled, filters.createdFrom, filters.createdTo])

  const syncOperational = useCallback(
    async (refresh = false) => {
      if (!enabled) return
      const seq = ++syncSeq.current
      setLoading(true)
      setError(null)

      try {
        const payload = await gestoresService.getDashboard({
          ...syncQuery,
          scope: 'operational',
          refresh,
        })
        if (seq !== syncSeq.current) return

        setBaseOperational(payload)
        if (refresh || financialPeriodKey !== periodKey) {
          setFinancialMetrics(null)
          setFinancialPeriodKey(null)
        }
        void refreshPresets()
      } catch (err) {
        if (seq !== syncSeq.current) return
        const message = getFriendlyError(err, 'Não foi possível carregar o dashboard eKyte')
        setError(message)
        setBaseOperational(null)
        toast.error(message)
      } finally {
        if (seq === syncSeq.current) setLoading(false)
      }
    },
    [enabled, financialPeriodKey, periodKey, refreshPresets, syncQuery],
  )

  const loadFinancial = useCallback(
    async (refresh = false) => {
      if (!enabled) return
      if (!refresh && financialPeriodKey === periodKey && financialMetrics) return

      const seq = ++financialSeq.current
      setFinancialLoading(true)
      try {
        const payload = await gestoresService.getDashboard({
          createdFrom: syncQuery.createdFrom,
          createdTo: syncQuery.createdTo,
          scope: 'financial',
          refresh,
        })
        if (seq !== financialSeq.current) return
        setFinancialMetrics(payload.financial)
        setFinancialPeriodKey(periodKey)
      } catch (err) {
        if (seq !== financialSeq.current) return
        toast.error(getFriendlyError(err, 'Não foi possível carregar os insights financeiros'))
      } finally {
        if (seq === financialSeq.current) setFinancialLoading(false)
      }
    },
    [enabled, financialMetrics, financialPeriodKey, periodKey, syncQuery.createdFrom, syncQuery.createdTo],
  )

  useEffect(() => {
    if (!enabled) return
    void refreshPresets()
  }, [enabled, refreshPresets])

  useEffect(() => {
    if (!enabled) return
    void syncOperational(false)
  }, [enabled, syncOperational])

  useEffect(() => {
    if (!enabled) return
    void gestoresService.getDashboardFilterCatalogs().then(setCatalogs).catch(() => undefined)
  }, [enabled])

  useEffect(() => {
    if (!enabled || defaultAppliedRef.current || presets.length === 0) return
    const defaultPreset = presets.find((preset) => preset.isDefault)
    if (!defaultPreset) return
    defaultAppliedRef.current = true
    setActivePresetId(defaultPreset.id)
    setFilters((prev) => ({ ...prev, ...presetCriteriaToFilters(defaultPreset.criteria) }))
  }, [enabled, presets])

  const data = useMemo(() => {
    if (!activeOperational) return null
    return applyDashboardClientFilters(activeOperational, clientFilters, financialMetrics)
  }, [activeOperational, clientFilters, financialMetrics])

  useEffect(() => {
    if (!activeOperational) return
    const allowedWorkspaces = new Set(filterOptions.workspaces.map((option) => option.value))
    const allowedExecutors = new Set(filterOptions.executors.map((option) => option.value))
    setFilters((prev) => {
      const workspaces = prev.workspaces.filter((value) => allowedWorkspaces.has(value))
      const executors = prev.executors.filter((value) => allowedExecutors.has(value))
      if (
        workspaces.length === prev.workspaces.length &&
        executors.length === prev.executors.length
      ) {
        return prev
      }
      return { ...prev, workspaces, executors }
    })
  }, [activeOperational, filterOptions.executors, filterOptions.workspaces])

  const setFilter = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setActivePresetId(null)
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const selectClientScope = useCallback((scope: 'workspace' | 'phase', value: string) => {
    setActivePresetId(null)
    setFilters((prev) => ({
      ...prev,
      workspaces: scope === 'workspace' ? [value] : [],
      phase: scope === 'phase' ? value : '',
    }))
  }, [])

  const clearClientScope = useCallback(() => {
    setActivePresetId(null)
    setFilters((prev) => ({ ...prev, workspaces: [], phase: '' }))
  }, [])

  const selectExecutorScope = useCallback((executor: string) => {
    setActivePresetId(null)
    setFilters((prev) => ({ ...prev, executors: [executor] }))
  }, [])

  const clearExecutorScope = useCallback(() => {
    setActivePresetId(null)
    setFilters((prev) => ({ ...prev, executors: [] }))
  }, [])

  const setPeriod = useCallback((createdFrom: string, createdTo: string) => {
    setActivePresetId(null)
    setFilters((prev) => ({
      ...prev,
      createdFrom,
      createdTo,
      workspaces: [],
      executors: [],
      phase: '',
    }))
  }, [])

  const clearFilters = useCallback(() => {
    const period = defaultDashboardPeriod()
    setActivePresetId(null)
    setFilters({
      createdFrom: period.createdFrom,
      createdTo: period.createdTo,
      squadId: null,
      squadName: '',
      workspaces: [],
      executors: [],
      taskType: '',
      phase: '',
      search: '',
    })
  }, [])

  const applyPreset = useCallback((preset: GestorDashboardFilterPreset) => {
    setActivePresetId(preset.id)
    setFilters((prev) => ({ ...prev, ...presetCriteriaToFilters(preset.criteria) }))
  }, [])

  const openSaveFilterModal = useCallback((preset?: GestorDashboardFilterPreset | null) => {
    setEditingPreset(preset ?? null)
    setSaveModalOpen(true)
  }, [])

  const saveFilterPreset = useCallback(
    async (input: { name: string; criteria: typeof currentCriteria; isDefault: boolean }) => {
      try {
        if (editingPreset) {
          await gestoresService.updateDashboardFilterPreset(editingPreset.id, input)
          toast.success('Filtro atualizado')
        } else {
          await gestoresService.createDashboardFilterPreset(input)
          toast.success('Filtro salvo')
        }
        setSaveModalOpen(false)
        setEditingPreset(null)
        await refreshPresets()
      } catch (err) {
        toast.error(getFriendlyError(err, 'Não foi possível salvar o filtro'))
      }
    },
    [editingPreset, refreshPresets],
  )

  const deleteFilterPreset = useCallback(async () => {
    if (!editingPreset) return
    try {
      await gestoresService.deleteDashboardFilterPreset(editingPreset.id)
      if (activePresetId === editingPreset.id) setActivePresetId(null)
      setSaveModalOpen(false)
      setEditingPreset(null)
      toast.success('Filtro excluído')
      await refreshPresets()
    } catch (err) {
      toast.error(getFriendlyError(err, 'Não foi possível excluir o filtro'))
    }
  }, [activePresetId, editingPreset, refreshPresets])

  const reload = useCallback(async () => {
    await syncOperational(true)
    if (financialPeriodKey === periodKey) await loadFinancial(true)
    await refreshPresets()
  }, [financialPeriodKey, loadFinancial, periodKey, refreshPresets, syncOperational])

  return {
    data,
    loading: loading || financialLoading,
    presetsLoading,
    error,
    filters,
    filterOptions,
    presets,
    catalogs,
    activePresetId,
    currentCriteria,
    saveModalOpen,
    editingPreset,
    setFilter,
    selectClientScope,
    clearClientScope,
    selectExecutorScope,
    clearExecutorScope,
    setPeriod,
    clearFilters,
    applyPreset,
    openSaveFilterModal,
    closeSaveFilterModal: () => {
      setSaveModalOpen(false)
      setEditingPreset(null)
    },
    saveFilterPreset,
    deleteFilterPreset,
    reload,
    loadFinancial,
  }
}
